import { StandardStatus } from '@zoocasa/go-search';
import { TFunction } from 'i18next';
import { LOCALE_COMMON_PATHS } from 'types/locales';
import styles from './style.module.scss';

export function getSoldPriceLabel(
  t: TFunction,
  isCurrentListing: boolean,
  isRental: boolean,
  isAvailable: boolean,
  soldPrice: number | null,
  formatCurrency: (value: number) => string
) {
  if (isCurrentListing && !soldPrice) {
    if (isRental) {
      return t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NOT_LEASED_YET);
    } else if (isAvailable) {
      return t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NOT_SOLD_YET);
    } else {
      return '-';
    }
  } else {
    if (isAvailable) {
      return t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NOT_SOLD_YET);
    } else {
      return soldPrice ? formatCurrency(soldPrice) : '-';
    }
  }
}

/**
 * Maps a listing status to the appropriate CSS class name for styling.
 *
 * This function determines the correct CSS module class based on the listing status
 * to apply appropriate visual styling (colors, borders, etc.) to the status badge.
 * Multiple statuses may map to the same CSS class for consistent visual grouping.
 *
 * @param status the standard status of the listing from the StandardStatus enum
 * @returns A CSS module class name string that corresponds to the status styling
 *
 * @example
 * ```tsx
 * const statusClass = getStatusClass(StandardStatus.STANDARD_STATUS_ACTIVE);
 * // Returns: styles['active'] (applies active status styling)
 *
 * const statusClass = getStatusClass(StandardStatus.STANDARD_STATUS_HOLD);
 * // Returns: styles['inactive'] (applies inactive status styling)
 * ```
 *
 * @remarks
 * - Active and Active Under Contract statuses both use 'active' styling
 * - Hold, Withdrawn, and Pending statuses all use 'inactive' styling
 * - Each status maps to one of: active, closed, expired, canceled, inactive, or other
 * - The returned class name corresponds to CSS rules in style.module.scss
 * - Falls back to 'other' styling if an unrecognized status is provided
 */
export function getStatusClass(status: StandardStatus) {
  let statusClass: string;
  switch (status) {
    case StandardStatus.STANDARD_STATUS_ACTIVE:
    case StandardStatus.STANDARD_STATUS_ACTIVE_UNDER_CONTRACT:
      statusClass = styles['active'];
      break;
    case StandardStatus.STANDARD_STATUS_CLOSED:
      statusClass = styles['closed'];
      break;
    case StandardStatus.STANDARD_STATUS_EXPIRED:
      statusClass = styles['expired'];
      break;
    case StandardStatus.STANDARD_STATUS_CANCELED:
      statusClass = styles['canceled'];
      break;
    case StandardStatus.STANDARD_STATUS_HOLD:
    case StandardStatus.STANDARD_STATUS_WITHDRAWN:
    case StandardStatus.STANDARD_STATUS_PENDING:
      statusClass = styles['inactive'];
      break;
    default:
      statusClass = styles['other'];
      break;
  }
  return statusClass;
}